import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Loading } from '../../../global/Loading/Loading'
import { ScrollToIds } from '../../../hooks/useScrollToElementIds'
import { FileIcon } from '../../../icons/FileIcon'
import { NameDto } from '../../../model/NameDto'
import { TransactionStatus } from '../../../model/TransactionStatus'
import { PageSection } from '../../../ui/v2/PageSection/PageSection'
import { Pagination } from '../../../ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '../../../ui/v2/TableFilter/TableFilter'
import { HeaderActions } from '../Common/HeaderActions'
import { RebatesSummary } from '../RebatesSummary'
import { EmptyTransactionsPageTable, TransactionsPageTable } from './TransactionsPageTable'
import { useIBRebateTransactions } from './hooks/useIBRebateTransactions'
import { useIBTradingAccountsRebateSearch } from './hooks/useIBTradingAccountsRebateSearch'
import { useIBTransactionsFilterOptions } from './hooks/useIBTransactionsFiltersOptions'
import { useIBRebateTransactionsSummary } from './hooks/useIBTransactionsSummary'

import styles from './TransactionsPage.module.scss'

export const IBRebatesTransactionsPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const {
    query: tableQuery,
    sortOptions: tableSortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
  } = useIBRebateTransactions()

  const { summaryItems, isLoading: isSummaryLoading } = useIBRebateTransactionsSummary(tableSearch)
  const { accountTypes, dateShortcutOptions } = useIBTransactionsFilterOptions()
  const { tradingAccountOptions, setSearch: setListSearch } = useIBTradingAccountsRebateSearch()

  const isTableLoading = tableQuery.isLoading

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const rebateTransactionStatusOptions: NameDto<TransactionStatus>[] = [
    {
      id: TransactionStatus.Completed,
      name: t('IB.RebatesPage.Paid'),
    },
    {
      id: TransactionStatus.AwaitingApproval,
      name: t('IB.RebatesPage.Pending'),
    },
    {
      id: TransactionStatus.Declined,
      name: t('IB.RebatesPage.Declined'),
    },
  ]

  return (
    <div className={styles.transactionsPage}>
      <RebatesSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />
      <div className={styles.container}>
        <PageSection
          icon={FileIcon}
          title={t('IB.RebatesPage.Transactions')}
          id={ScrollToIds.IBRebateTransactionsHeader}
        >
          <HeaderActions onFiltersClick={() => setIsFilterVisible((prevState) => !prevState)} />
        </PageSection>
        <TableFilter
          isVisible={isFilterVisible}
          searchFilters={tableSearch}
          dateShortcutOptions={dateShortcutOptions}
          statusOptions={rebateTransactionStatusOptions}
          tradingAccountSearchProps={{
            options: tradingAccountOptions,
            setSearch: (search) => search && setListSearch(search),
          }}
          tradingAccountTypeOptions={accountTypes || []}
          filterOptions={[
            FilterOptions.TransactionDate,
            FilterOptions.TradingAccountId,
            FilterOptions.TradingAccountType,
            FilterOptions.AccumulatedVolumeFxm,
            FilterOptions.AccumulatedVolumeOther,
            FilterOptions.AccumulatedCommission,
            FilterOptions.RebateAmount,
            FilterOptions.Status,
          ]}
          setSearchFilters={(searchFilters) => {
            if (!searchFilters) {
              return
            }
            setTableSearch(searchFilters)
            tableQuery.setPageQuery?.({
              ...tableQuery.pageQuery,
              pageIndex: 1,
            })
          }}
        />
        <div className={styles.tableContainer}>
          <Loading isLoading={isTableLoading} showLoadingIcon>
            {tableQuery.data.length ? (
              <TransactionsPageTable
                rebateTransactions={tableQuery.data}
                sortOptions={tableSortOptions}
                pageQuery={tableQuery.pageQuery}
                sticky='left'
                setPageQuery={tableQuery.setPageQuery}
              />
            ) : (
              <EmptyTransactionsPageTable />
            )}
            {tableQuery.meta && tableQuery.hasInitialResults && (
              <Pagination
                scrollToHeaderId={ScrollToIds.IBClientsHeader}
                maxPageSize={100}
                pageData={tableQuery.meta}
                isLoading={tableQuery.isLoading}
                onPageChanged={handlePageChange}
              />
            )}
          </Loading>
        </div>
      </div>
    </div>
  )
}
